import React, { useEffect, useState } from 'react';
import './blogPostPage.scss';
import { useParams } from 'react-router-dom';
import PagesHeroSection from '../../components/pagesHeroSection';
import { Container, Spinner } from 'react-bootstrap';

import { collection, query, where, getDocs } from 'firebase/firestore/lite';
import { db } from '../../firebase/firebase';
import emptyBlogImg from '../../assets/blog/no-blog-img.png';

const BlogPostPage = () => {
  const { blogTitle, lang } = useParams();

  // scripts/inject-blog-posts.js embeds this post's data in each static
  // article page as window.__BLOG_POST__. Starting from it (instead of an
  // empty "loading" state) means hydration keeps the prerendered article
  // on screen and Googlebot's render never depends on a Firestore round
  // trip. Before this, hydration swapped the article for a spinner and
  // re-fetched it - when that fetch was slow or failed in Google's renderer
  // the page read as "Loading article..." / "Article Not Found", which is
  // why Search Console flagged ~76 posts as Soft 404.
  const preloaded =
    typeof window !== 'undefined' &&
    window.__BLOG_POST__ &&
    window.__BLOG_POST__.slug === blogTitle
      ? window.__BLOG_POST__
      : null;

  const [blog, setBlog] = useState(preloaded);
  const [loading, setLoading] = useState(!preloaded);
  const [error, setError] = useState(false);
  const [postDates, setPostDates] = useState(
    preloaded && (preloaded.datePublished || preloaded.dateModified)
      ? {
          ...(preloaded.datePublished && { datePublished: preloaded.datePublished }),
          ...(preloaded.dateModified && { dateModified: preloaded.dateModified }),
        }
      : null,
  );

  // scripts/inject-blog-posts.js bakes a real datePublished/dateModified
  // (Firestore's own createTime/updateTime for this post) into the schema
  // of the STATIC file react-snap/that script generates for this URL - but
  // this component's own client-side re-render below rebuilds the same
  // schema from scratch using data from the Firestore SDK, which has no
  // createTime/updateTime of its own (see that script's comments). Without
  // this, the dates would only last until hydration, then vanish the
  // moment this component's fetch resolves and replaces the script tag.
  // blog-dates.json is a small static file (not Firestore, no API key)
  // written by that same build script - {slug: {datePublished,
  // dateModified}} - so the client picks up the identical two values a
  // search engine would see whether it reads the static HTML or waits for
  // JS to render first. Missing file (e.g. local `npm start` without a
  // postbuild step) or missing entry both just leave these fields absent -
  // never fabricated.
  useEffect(() => {
    if (preloaded) return undefined;
    let cancelled = false;
    fetch('/blog-dates.json')
      .then((res) => (res.ok ? res.json() : {}))
      .then((allDates) => {
        if (!cancelled) setPostDates(allDates[blogTitle] || null);
      })
      .catch(() => {
        if (!cancelled) setPostDates(null);
      });
    return () => {
      cancelled = true;
    };
  }, [blogTitle, preloaded]);

  useEffect(() => {
    if (preloaded) return;
    const fetchBlog = async () => {
      try {
        // Was `doc(db, 'blogs', blogTitle)` - a direct document-ID lookup,
        // which only works when a post's Firestore document ID happens to
        // match its `slug` field exactly. That's true for 24 of the 25
        // posts, but one document's ID is literally "new fees for asylum
        // and work permits" (spaces and all - confirmed in the Firebase
        // console 28 Aug 2026), which is also where that broken URL came
        // from. Querying by the `slug` FIELD instead means the URL a
        // visitor lands on only ever has to match the slug field - not
        // Firestore's own internal document ID - so fixing that one post's
        // slug is a single-field edit in the console, with no need to
        // recreate the document under a new ID (and risk losing its
        // content) just to keep this lookup working.
        const q = query(collection(db, 'blogs'), where('slug', '==', blogTitle));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          setBlog(snapshot.docs[0].data());
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching blog:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [blogTitle, preloaded]);

  const title = blog?.title?.[lang] || blog?.title?.en || 'Blog';
  const description = blog?.description?.[lang] || blog?.description?.en || '';
  const heroImage = blog?.imgUrl || emptyBlogImg;

  // Was: `if (loading) return <div className="text-center py-5">...</div>`
  // and `if (error) return <div className="text-center py-5"><PagesHeroSection .../>...</div>`
  // - two early returns, each with an outer shape completely different from
  // the loaded article below (no <article className="blog-post-page">, no
  // schema script, and the error branch even mounted a SECOND, differently-
  // configured PagesHeroSection). This is the exact structural-mismatch
  // pattern already found and fixed in blogPage.jsx (see the comment there):
  // whatever a fresh visitor's browser renders FIRST (using this component's
  // *initial* state, before the Firestore fetch resolves) is what has to
  // line up with any prerendered/static HTML at this URL. A future static
  // snapshot of an article page - generated at build time the same way
  // scripts/inject-blog-content.js already does for the listing page - would
  // bake in the *loaded* shape, so the initial client render needs to reach
  // that same <article className="blog-post-page"> / hero / Container shell
  // immediately too, not after an early return. Keeping one consistent
  // outer shell across loading/error/loaded and only swapping the inner
  // content (same fix shape as BlogPage) avoids reintroducing #418 here.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: description,
    image: heroImage,
    // See the postDates useEffect above and scripts/inject-blog-posts.js
    // for why these come from a static JSON file instead of `blog` itself,
    // and why author stays the Organization (the firm's own call, 6 Sep
    // 2026 - not every post is attributed to one attorney internally).
    ...(postDates?.datePublished && { datePublished: postDates.datePublished }),
    ...(postDates?.dateModified && { dateModified: postDates.dateModified }),
    author: {
      '@type': 'Organization',
      name: 'Avagyan Law Firm',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Avagyan Law Firm',
      logo: {
        '@type': 'ImageObject',
        // Was '/logo.png', which 404s - avagyanlaw.com has no file by that
        // name. structuredData.jsx's own Organization schema already uses
        // /logo512.png (the real, existing file); matching that here.
        url: 'https://avagyanlaw.com/logo512.png',
      },
    },
    // Canonical URL, not window.location.href: a visit with ?utm_... or a
    // #hash would otherwise differ from the static page's schema and break
    // hydration (React #418), and the schema should name the canonical page.
    mainEntityOfPage: `https://avagyanlaw.com/${lang}/blog/${blogTitle}/`,
  };

  return (
    <article className="blog-post-page">
      {!loading && !error && <script type="application/ld+json">{JSON.stringify(schema)}</script>}

      <PagesHeroSection page={loading ? '' : error ? 'Blog Not Found' : title} background={heroImage} />

      <Container className="py-5">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">Loading article...</p>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-5">
            <h1>Article Not Found</h1>
            <p>The blog article you are looking for does not exist.</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* h2 styled as h1: the hero above already renders the page's
                single <h1> with this same title. */}
            <header className="blog-header">
              <h2 className="h1">{title}</h2>
            </header>

            <section className="blog-body">
              <p className="blog-description">{description}</p>
            </section>
          </>
        )}
      </Container>
    </article>
  );
};

export default BlogPostPage;
